import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import { Alert, Button, Card, CardContent, Grid } from '@mui/material';
import Delete from '@mui/icons-material/Delete';

import ApprovalAutomationsSection from '../../waterAuthoritySubmissions/components/ApprovalAutomationsSection';
import BackflowJobGroupInfoHeader from '../components/BackflowJobGroupInfoHeader';
import CreateBackflowJobItemModal from '../components/CreateBackflowJobItemModal';
import DataTable from '../../../sharedModules/dataTable';
import DeleteConfirmationModal from '../../../sharedModules/layout/DeleteConfirmationModal';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import ShakingAlert, { ShakingAlertHandle } from '../../../sharedModules/layout/ShakingAlert';
import StatusCell from '../../../sharedModules/layout/StatusCell';
import WaterAuthorityUpdatingFields from '../../waterAuthorityConfig/components/WaterAuthorityUpdatingFields';

import { minimumLoading } from '../../../utils/minimumLoading';

import { BackflowJobGroup, BackflowJobItem } from '../../model';
import { saveJobGroupToCache, loadJobGroupFromCache } from '../../utils/backflowCache';

import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';
import { DeleteConfirmationModalContext } from '../../../sharedModules/contexts/DeleteConfirmationModalContext';

import axios from '../../../sharedModules/utils/axios.utils';

const BackflowJobGroupDetailPage: React.FC = () => {
  const { backflowJobGroupId, formAction } = useParams<{ backflowJobGroupId: string; formAction?: string }>();
  const history = useHistory();

  const { user } = useContext(AuthenticationContext);
  const { setModelLabel, setDeleteConfirmationModalOpen, setRecordToDelete, setDeleteURL } =
    useContext(DeleteConfirmationModalContext);

  const [backflowJobGroup, setBackflowJobGroup] = useState<BackflowJobGroup>({});
  const [backflowJobItems, setBackflowJobItems] = useState<BackflowJobItem[]>();
  const [createJobItemModalOpen, setCreateJobItemModalOpen] = useState<boolean>(false);
  const [jobErrorMessage, setJobErrorMessage] = useState<string>('');
  const [itemsErrorMessage, setItemsErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadedFromCache, setLoadedFromCache] = useState<boolean>(false);

  const shakingAlertRef = useRef<ShakingAlertHandle>(null);

  const handleDelete = (backflowJobGroup) => {
    setModelLabel('Device');
    setDeleteConfirmationModalOpen(true);
    setRecordToDelete(backflowJobGroup);
    setDeleteURL('backflows/backflow_job_items/');
  };

  // Fetch dropdown options for the device form
  const fetchFormOptions = async () => {
    try {
      const [eqTypeResponse, bfpManufacturerResponse, wmManufacturerResponse] = await Promise.all([
        axios.get<string, any>('/pick_list_options', {
          params: { model: 'ServmanEqTypes', 'ransack[servman_type_i_cont]': 'Backflow Preventer' },
        }),
        axios.get<string, any>('/pick_list_options', {
          params: { model: 'Manufacturer', 'ransack[manufacturer_types_name_i_cont]': 'Backflow Preventer' },
        }),
        axios.get<string, any>('/pick_list_options', {
          params: { model: 'Manufacturer', 'ransack[manufacturer_types_name_i_cont]': 'Water Meter' },
        }),
      ]);

      return {
        eqTypeOptions: eqTypeResponse.data.result,
        manfBfpOptions: bfpManufacturerResponse.data.result,
        manfWmOptions: wmManufacturerResponse.data.result,
      };
    } catch (error) {
      console.error('Failed to fetch form options:', error);
      return null;
    }
  };

  // Save form options to sessionStorage (shared across all job groups)
  const saveFormOptionsToSession = (formOptions: any) => {
    try {
      if (formOptions) {
        sessionStorage.setItem('backflowFormOptions', JSON.stringify(formOptions));
      }
    } catch (error) {
      console.error('Failed to save form options to sessionStorage:', error);
    }
  };

  // Asset Number, Location, Model NO, Size, (Result), Tech, Status on Table

  const backflowJobItemsColumns = [
    {
      name: 'Asset No',
      selector: 'asset_no',
      sortable: true,
    },
    {
      name: 'Location',
      selector: 'location',
      sortable: true,
    },
    {
      name: 'Model No',
      selector: 'model_no',
      sortable: true,
    },
    {
      name: 'Size',
      selector: 'backflow_size',
      sortable: true,
      cell: (row: any) => (row.backflow_size ? `${row.backflow_size} inch` : 'Unknown'),
    },
    {
      name: 'Result',
      selector: 'pass_or_fail',
      sortable: true,
      cell: (row: any) => <StatusCell status={row.pass_or_fail} />,
    },
    {
      name: 'Logging Tech',
      selector: 'display_emp_id',
      sortable: true,
    },
    {
      name: 'Status',
      selector: 'status',
      sortable: true,
      cell: (row) => <StatusCell status={row.status} />,
    },
    {
      name: 'Actions',
      selector: 'actions',
      grow: 0.5,
      cell: (row) => (
        <Button variant="contained" color="warning" onClick={() => handleDelete(row)} disabled={!row.allow_delete}>
          <Delete />
        </Button>
      ),
    },
  ];

  const fetchBackflowJobGroup = async () => {
    const response: any = await axios.get(`/backflows/backflow_job_groups/${backflowJobGroupId}`);
    setBackflowJobGroup(response.data.result);
    return response.data.result;
  };

  const fetchBackflowJobItems = async () => {
    const response: any = await axios.get('/backflows/backflow_job_items', {
      params: { 'ransack[backflow_job_group_id_eq]': backflowJobGroupId },
    });
    setBackflowJobItems(response.data.result);
    return response.data.result;
  };

  const fetchAndCacheJobGroupData = async () => {
    try {
      // Fetch job group, items, and form options
      const [jobGroup, jobItems, formOptions] = await Promise.all([
        fetchBackflowJobGroup(),
        fetchBackflowJobItems(),
        fetchFormOptions(),
      ]);

      // Save form options to sessionStorage (shared across all job groups)
      saveFormOptionsToSession(formOptions);

      // Save job group data to localStorage
      saveJobGroupToCache(backflowJobGroupId, jobGroup, jobItems);
      setLoadedFromCache(false);

      setJobErrorMessage('');
      setItemsErrorMessage('');
    } catch (error: any) {
      // Determine if this is a network error or a server error
      const isNetworkError = !navigator.onLine || error.code === 'ERR_NETWORK' || error.message === 'Network Error';

      if (isNetworkError) {
        // Network issue - try to load from cache
        const cached = loadJobGroupFromCache(backflowJobGroupId);

        if (cached) {
          setBackflowJobGroup(cached.jobGroup);
          setBackflowJobItems(cached.jobItems);
          setLoadedFromCache(true);
          setJobErrorMessage('');
          setItemsErrorMessage('');
        } else {
          setJobErrorMessage('No internet connection and no cached data available.');
          setItemsErrorMessage('');
        }
      } else {
        // Server error (500, 404, auth error, etc.) - show error messages
        const statusCode = error.response?.status;

        if (statusCode === 404) {
          setJobErrorMessage('Job group not found.');
        } else if (statusCode === 403) {
          setJobErrorMessage('You do not have permission to view this job group.');
        } else if (statusCode >= 500) {
          setJobErrorMessage('Server error. Please try again later.');
        } else {
          setJobErrorMessage('Failed to load job group. Please try again.');
        }

        setItemsErrorMessage('');
        console.error('Server error fetching job group data:', error);
      }
    }
  };

  useEffect(() => {
    if (backflowJobGroupId && user) {
      minimumLoading(fetchAndCacheJobGroupData, setLoading);
    }
  }, [backflowJobGroupId, user]);

  const showWaterAuthorityWarning = () => {
    if (formAction !== 'approve') return false;
    if (backflowJobGroup?.water_authority_name === 'Unknown') return true;
    if (backflowJobGroup?.authority_provided_values_complete === 'missing') return true;
    return false;
  };

  const handleRowClicked = (row: any) => {
    if (showWaterAuthorityWarning()) {
      shakingAlertRef.current?.shake();
      return;
    }

    if (formAction) {
      history.push(`/backflow_tests/backflow_form/${row.id}/${formAction}`);
    } else {
      history.push(`/backflow_tests/backflow_form/${row.id}`);
    }
  };

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <CreateBackflowJobItemModal
        backflowJobGroup={backflowJobGroup}
        fetchBackflowJobItems={fetchBackflowJobItems}
        fetchBackflowJobGroup={fetchBackflowJobGroup}
        createJobItemModalOpen={createJobItemModalOpen}
        setCreateJobItemModalOpen={setCreateJobItemModalOpen}
      />
      <DeleteConfirmationModal afterDelete={fetchBackflowJobItems} />
      <Grid container spacing={2} style={{ padding: '1% 2%' }}>
        <Grid item xs={12}>
          <PageHeader
            titleIcon={<PlumbingIcon />}
            mainTitle="Backflow Testing"
            subTitle={`${backflowJobGroup.company_name} (Order: ${backflowJobGroup.order_no})`}
            backButtonLabel={formAction === 'approve' ? 'Approvals Queue' : 'All Backflow Tests'}
            backButtonLink={formAction === 'approve' ? '/backflow_tests/approvals' : '/backflow_tests/search'}
          />
        </Grid>
        {loadedFromCache && (
          <Grid item xs={12}>
            <Alert severity="info">
              You are currently offline. Any changes you make will be saved locally and can be submitted when you have
              connectivity.
            </Alert>
          </Grid>
        )}
        <Grid item xs={12} style={{ marginTop: 10 }}>
          <BackflowJobGroupInfoHeader backflowJobGroup={backflowJobGroup} />
        </Grid>
        {showWaterAuthorityWarning() && (
          <>
            <Grid item xs={12} style={{ marginBottom: 10 }}>
              <ShakingAlert ref={shakingAlertRef} message="Water Authority Info Missing." severity="warning" />
            </Grid>
            <Grid item xs={6} style={{ marginBottom: 10 }}>
              <WaterAuthorityUpdatingFields
                backflowJobGroup={backflowJobGroup}
                fetchBackflowJobGroup={fetchBackflowJobGroup}
              />
            </Grid>
          </>
        )}
        <ApprovalAutomationsSection backflowJobGroupId={backflowJobGroupId ?? ''} />
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <Card>
            <CardContent>
              <DataTable
                title="Backflow Test Assets"
                columns={backflowJobItemsColumns}
                data={backflowJobItems || []}
                onRowClicked={handleRowClicked}
                dense
                highlightOnHover
                pointerOnHover={!showWaterAuthorityWarning()}
                striped
                responsive
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={jobErrorMessage} />
          <ErrorMessage message={itemsErrorMessage} />
        </Grid>

        {formAction === 'approve' ? (
          <Grid item xs={6}>
            <WaterAuthorityUpdatingFields
              backflowJobGroup={backflowJobGroup}
              fetchBackflowJobGroup={fetchBackflowJobGroup}
            />
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Button variant="contained" color="primary" onClick={() => setCreateJobItemModalOpen(true)}>
              Add Asset to Backflow Test
            </Button>
          </Grid>
        )}
      </Grid>
    </LoadingWrapper>
  );
};

export default BackflowJobGroupDetailPage;
